// Payout-history ledger — /analytics/orgs/{org_id}/payouts. An ORG OWNER's
// record of actual Stripe payouts (what HAS been / will be disbursed to their
// bank), shown beside the accrual-based settlement view in the Earnings
// workspace. Settlements answer "what am I OWED?"; this answers "what was PAID
// OUT?".
//
// THIN presentation layer mirroring SettlementLedger (Card/CardHeader, table,
// tabular-nums, Badge, Spinner, honest states): every field — amount (a SIGNED
// Money), status, arrival/created dates, failure_code — is a real backend field
// rendered via the shared money/date formatters. There is NO client-side money
// math (a re-sum of the rows could diverge and lie); figures are the endpoint's
// values VERBATIM, and an opaque id (payout_id / stripe_payout_id) is NEVER
// rendered (no-UID-in-UI).
//
// Unlike the facility-scoped settlement fetch the parent workspace owns, this
// view is ORG-scoped against a separate endpoint, so the component owns its OWN
// query (keyed by orgId, enabled only once an org id is resolved). The parent
// passes the resolved orgId and gates rendering on it.
//
// RBAC nuance: a non-owner facility manager CAN see settlements but gets a 403
// here (payout history is owner / internal-platform-admin only). A 403 degrades
// to an honest owner-only note — NOT the retryable "couldn't load" alert, NOT a
// crash, NOT settlement data. Any other failure shows the retryable error
// affordance, mirroring the settlement ledger.

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@bw/shared/api";
import { formatDate, formatMoney } from "@bw/shared/utils";

import {
  getOrgPayouts,
  type PayoutLedgerEntryResponse,
} from "@/libs/analyticsApi";

/** "in_transit" -> "In transit", "paid" -> "Paid". Truthful to the token; the
 * raw machine token (with underscores) is NEVER shown verbatim. */
function humanizeStatus(status: string): string {
  const normalized = status.trim().replace(/_/g, " ");
  if (!normalized) return status;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/** A terminal paid status reads as success, a failed payout as error, and
 * everything in flight stays neutral. */
function chipColorFor(status: string): "success" | "error" | "default" {
  const s = status.trim().toLowerCase();
  if (s === "paid") return "success";
  if (s === "failed" || s === "canceled" || s === "cancelled") {
    return "error";
  }
  return "default";
}

/**
 * Payout history for an org. `orgId` is resolved by the parent (via
 * useMyOrg().org?.org_id) and may be null while the org is still resolving — the
 * query is disabled until a truthy id is known, so a null org fires ZERO reads.
 */
export function PayoutLedger({ orgId }: { orgId: string | null }) {
  // Self-contained org-scoped fetch (the parent's settlement fetch is
  // facility-scoped and carries no payout data). Disabled until an org id is
  // resolved, mirroring the enabled-gating used across the app's queries.
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["analytics", "org-payouts", orgId],
    queryFn: () => getOrgPayouts(orgId as string),
    enabled: !!orgId,
  });

  // A null org renders nothing (the parent also gates on orgId, but guard here
  // so the component is safe rendered in isolation): no read, no content.
  if (!orgId) return null;

  // RBAC: a 403 means the caller can see settlements but not payouts (owner /
  // internal-platform-admin only). Degrade to an honest owner-only note — never
  // the retryable alert, never the raw backend message verbatim.
  const isForbidden = error instanceof ApiError && error.status === 403;

  return (
    <Paper component="section" sx={{ overflow: "hidden" }} variant="outlined">
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <Typography component="h2" variant="h6">
          Payout history
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
        {isLoading ? (
          <Stack
            aria-label="Loading payout history"
            role="status"
            spacing={1}
            sx={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: 128,
            }}
          >
            <CircularProgress aria-hidden size={24} />
            <Typography color="text.secondary" variant="body2">
              Loading payout history…
            </Typography>
          </Stack>
        ) : isForbidden ? (
          // Distinct from the retryable error state below: a 403 is not
          // retryable, so it carries no Retry button and no "couldn't load" copy.
          <Typography color="text.secondary" variant="body2">
            Payout history is visible to the organization owner.
          </Typography>
        ) : isError ? (
          <Alert
            action={
              <Button
                color="inherit"
                onClick={() => void refetch()}
                size="small"
              >
                Retry
              </Button>
            }
            severity="error"
          >
            We couldn&apos;t load payout history.
          </Alert>
        ) : (data?.payouts?.length ?? 0) === 0 ? (
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: 128,
            }}
          >
            <Typography color="text.secondary" variant="body2">
              No payouts yet.
            </Typography>
          </Stack>
        ) : (
          <PayoutTable
            payouts={data?.payouts ?? []}
            envelopeCurrency={data?.currency ?? null}
          />
        )}
      </Box>
    </Paper>
  );
}

/**
 * The payout rows. Each row formats its amount in its OWN `currency` (falling
 * back to the envelope currency), so a mixed-currency response (envelope
 * currency null) still renders honest per-row figures. The date column reads
 * "Paid out {arrival_date}" once a payout has landed, and an honest "Pending"
 * before then — never a fabricated date for a null arrival_date.
 */
function PayoutTable({
  payouts,
  envelopeCurrency,
}: {
  payouts: PayoutLedgerEntryResponse[];
  envelopeCurrency: string | null;
}) {
  return (
    <TableContainer>
      <Table aria-label="Payout history" size="small">
        <TableHead>
          <TableRow>
            <TableCell scope="col" sx={{ fontWeight: 700 }}>
              Payout
            </TableCell>
            <TableCell align="right" scope="col" sx={{ fontWeight: 700 }}>
              Amount
            </TableCell>
            <TableCell scope="col" sx={{ fontWeight: 700 }}>
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payouts.map((payout) => {
            // Row currency takes precedence so a mixed-currency response (no
            // single envelope currency) still renders each row honestly.
            const currency = payout.currency ?? envelopeCurrency ?? "USD";
            // "Paid out <date>" once landed; an honest "Pending" before — a null
            // arrival_date must NOT fabricate a concrete date.
            const dateLabel = payout.arrival_date
              ? `Paid out ${formatDate(payout.arrival_date)}`
              : "Pending";
            return (
              <TableRow key={payout.payout_id}>
                <TableCell sx={{ color: "text.secondary" }}>
                  {dateLabel}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}
                >
                  {formatMoney(payout.amount, currency)}
                </TableCell>
                <TableCell>
                  <Chip
                    color={chipColorFor(payout.status)}
                    label={humanizeStatus(payout.status)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
